<h2 mat-dialog-title>{{ data.title }}</h2>

<mat-dialog-content class="dialog-content">
  <form [formGroup]="form" class="dynamic-form">

    @for (field of data.fields; track field.key) {
      <!-- Visibilidad condicional: el campo sólo se muestra cuando el
      campo padre (visibleWhen.field) tiene el valor esperado. -->
      @if (isFieldVisible(field)) {

        @switch (field.type) {

          <!-- Inputs de texto, rfid, número y email comparten el mismo template -->
          @case ('text') {
            <ng-container *ngTemplateOutlet="inputTemplate; context: { field: field, type: 'text' }"></ng-container>
          }

          @case ('rfid') {
            <ng-container *ngTemplateOutlet="inputTemplate; context: { field: field, type: 'text' }"></ng-container>
          }

          <!-- Texto largo (descripción del servicio, recomendaciones…) -->
          @case ('textarea') {
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>{{ field.label }}</mat-label>
              <textarea matInput
                [formControlName]="field.key"
                cdkTextareaAutosize
                [cdkAutosizeMinRows]="field.rows ?? 4"
              [cdkAutosizeMaxRows]="field.maxRows ?? 12"></textarea>
              @if (form.get(field.key)?.hasError('required')) {<mat-error>Este campo es requerido</mat-error>}
            </mat-form-field>
          }

          @case ('number') {
            <ng-container *ngTemplateOutlet="inputTemplate; context: { field: field, type: 'number' }"></ng-container>
          }

          @case ('email') {
            <ng-container *ngTemplateOutlet="inputTemplate; context: { field: field, type: 'email' }"></ng-container>
          }

          <!-- Selector -->
          @case ('select') {
            @if (isSearchableSelect(field)) {
              <!-- Select con búsqueda (autocomplete). El valor se restringe
              a la lista; el texto libre que no coincide queda inválido. -->
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>{{ field.label }}</mat-label>
                <input
                  matInput
                  type="text"
                  placeholder="Buscar..."
                  [formControlName]="field.key"
                  [matAutocomplete]="selectAuto" />
                <mat-icon matSuffix>arrow_drop_down</mat-icon>
                <mat-autocomplete
                  #selectAuto="matAutocomplete"
                  [displayWith]="displayFn(field.options ?? [])">
                  @for (opt of (selectFilteredOptions(field) | async) ?? field.options; track opt.value) {
                    <mat-option [value]="opt.value">{{ opt.label }}</mat-option>
                  }
                </mat-autocomplete>
                @if (form.get(field.key)?.hasError('required')) {<mat-error>Este campo es requerido</mat-error>} @else if (form.get(field.key)?.hasError('notInList')) {<mat-error>Seleccione una opción válida de la lista</mat-error>}
              </mat-form-field>
            } @else {
              <!-- Selector simple (pocas opciones / enums fijos). -->
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>{{ field.label }}</mat-label>
                <mat-select [formControlName]="field.key">
                  @for (opt of field.options; track opt.value) {
                    <mat-option [value]="opt.value">{{ opt.label }}</mat-option>
                  }
                </mat-select>
                @if (form.get(field.key)?.hasError('required')) {<mat-error>Este campo es requerido</mat-error>}
              </mat-form-field>
            }
          }

          <!-- Selector múltiple -->
          @case ('multiselect') {
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>{{ field.label }}</mat-label>
              <mat-select [formControlName]="field.key" multiple>
                <mat-option class="select-all-option" value="all">
                  <mat-checkbox
                    [checked]="isAllCheck(field)"
                    [indeterminate]="isSomeChecked(field)"
                    (click)="$event.stopPropagation(); CheckAll(field)">
                    Seleccionar Todo
                  </mat-checkbox>
                </mat-option>
                @for (opt of field.options; track opt.value) {
                  <mat-option [value]="opt.value">{{ opt.label }}</mat-option>
                }
              </mat-select>
              @if (form.get(field.key)?.hasError('required')) {<mat-error>Este campo es requerido</mat-error>}
            </mat-form-field>
          }

          <!-- Autocomplete -->
          @case ('autocomplete') {
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>{{ field.label }}</mat-label>
              <input
                type="text"
                placeholder="Seleccione"
                [attr.aria-label]="field.label"
                matInput
                [formControlName]="field.key"
                [matAutocomplete]="auto"
                />
              <mat-autocomplete #auto="matAutocomplete">
                @for (opt of field.options; track opt.value) {
                  <mat-option [value]="opt.value">{{ opt.label }}</mat-option>
                }
              </mat-autocomplete>
              @if (form.get(field.key)?.hasError('required')) {<mat-error>Este campo es requerido</mat-error>}
            </mat-form-field>
          }

          <!--Autocomplete múltiple con chips-->
          @case ('multiautocomplete'){
            <div class="multiautocomplete-wrapper full-width">
              <label class="multiautocomplete-label">{{ field.label }}</label>

              <!-- Chips de valores seleccionados -->
              <mat-chip-set class="chip-list">
                @for (val of form.get(field.key)?.value; track $index) {
                  <mat-chip
                    [removable]="true"
                    (removed)="removeMultiOption(field, val)">
                    {{ getOptionLabel(field, val) }}
                    <mat-icon matChipRemove>cancel</mat-icon>
                  </mat-chip>
                }
              </mat-chip-set>

              <!-- Input de búsqueda -->
              <mat-form-field appearance="outline" class="full-width" style="margin-top: 6px;">
                <input
                  #multiInput
                  matInput
                  type="text"
                  placeholder="Buscar..."
                  [value]="multiInputValues.get(field.key) ?? ''"
                  (input)="onMultiInputChange(field, multiInput.value)"
                  [matAutocomplete]="multiAuto"
                  >
                <mat-icon matSuffix>search</mat-icon>
                <mat-autocomplete
                  #multiAuto="matAutocomplete"
                  (optionSelected)="addMultiOption(field, $event.option.value); multiInput.value = ''"
                  [displayWith]="displayEmpty">
                  @for (opt of multiFilteredOptions.get(field.key) | async; track opt) {
                    <mat-option
                      [value]="opt.value">
                      {{ opt.label }}
                    </mat-option>
                  }
                </mat-autocomplete>
              </mat-form-field>

              @if (form.get(field.key)?.touched && form.get(field.key)?.hasError('required')) {<mat-error class="multiautocomplete-error">
                  Este campo es requerido
                </mat-error>}
            </div>
          }

          <!-- Radio -->
          @case ('radio') {
            <mat-form-field>
              <mat-label>{{ field.label }}</mat-label>
              <mat-radio-group [formControlName]="field.key">
                @for (opt of field.options; track opt.value) {
                  <mat-radio-button [value]="opt.value">{{ opt.label }}</mat-radio-button>
                }
              </mat-radio-group>
              @if (form.get(field.key)?.hasError('required')) {<mat-error>Este campo es requerido</mat-error>}
            </mat-form-field>
          }

          <!-- Fecha -->
          @case ('date') {
            <mat-form-field appearance="outline" class="half-width">
              <mat-label>{{ field.label }}</mat-label>
              <input matInput [matDatepicker]="picker" [formControlName]="field.key" />
              <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
              <mat-datepicker #picker></mat-datepicker>
              @if (form.get(field.key)?.hasError('required')) {<mat-error>Este campo es requerido</mat-error>}
            </mat-form-field>
          }

          <!-- Hora en formato 24 hrs: selector de Hora (00-23) y Minutos
          (00-59). Independiente del idioma del navegador; guarda HH:mm. -->
          @case ('time') {
            <div class="time-field full-width">
              <label class="time-label">{{ field.label }}</label>
              <div class="time-selects">
                <mat-form-field appearance="outline" class="time-cell">
                  <mat-label>Hora</mat-label>
                  <mat-select [value]="timePart(field, 0)"
                    (selectionChange)="setTimePart(field, 0, $event.value)">
                    @for (h of hourOptions; track h) {
                      <mat-option [value]="h">{{ h }}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>
                <span class="time-colon">:</span>
                <mat-form-field appearance="outline" class="time-cell">
                  <mat-label>Min</mat-label>
                  <mat-select [value]="timePart(field, 1)"
                    (selectionChange)="setTimePart(field, 1, $event.value)">
                    @for (m of minuteOptions; track m) {
                      <mat-option [value]="m">{{ m }}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>
                <span class="time-hint">24 hrs</span>
              </div>
            </div>
          }

          <!-- Botón de acción -->
          @case ('button') {
            <div class="button-container">
              <button
                mat-raised-button
                color="accent"
                type="button"
                [matTooltip]="field.label"
                (click)="onActionClick(field.action)">
                <mat-icon>{{ field.icon }}</mat-icon>
              </button>
            </div>
          }

          <!--
          Servicios hospitalarios con sub dirección.
          Cada fila es un par { service, subDireccion } y se renderiza
          como un dropdown del catálogo + un input de texto libre para
          decir DÓNDE dentro del sitio se encuentra ese servicio.
          -->
          @case ('services') {
            <div class="services-wrapper full-width">
              <label class="services-label">{{ field.label }}</label>

              @for (row of getServiceRows(field); track $index) {
                <div class="services-row">
                  <mat-form-field appearance="outline" class="services-item">
                    <mat-label>{{ serviceItemLabel(field) }}</mat-label>
                    <mat-select
                      [value]="row[serviceItemKey(field)]"
                      (selectionChange)="updateServiceItem(field, $index, $event.value)">
                      @for (opt of serviceRowOptions(field, $index); track opt.value) {
                        <mat-option [value]="opt.value">{{ opt.label }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="services-detail">
                    <mat-label>{{ serviceDetailLabel(field) }}</mat-label>
                    <input
                      matInput
                      type="text"
                      [value]="row[serviceDetailKey(field)] ?? ''"
                      (input)="updateServiceDetail(field, $index, $any($event.target).value)" />
                  </mat-form-field>

                  <button
                    mat-icon-button
                    color="warn"
                    type="button"
                    matTooltip="Quitar servicio"
                    (click)="removeServiceRow(field, $index)">
                    <mat-icon>delete</mat-icon>
                  </button>
                </div>
              }

              <button
                mat-stroked-button
                color="primary"
                type="button"
                class="services-add-btn"
                (click)="addServiceRow(field)">
                <mat-icon>add</mat-icon>
                Agregar servicio
              </button>
            </div>
          }

          <!--
          Repeater genérico (usado por 'contacts': N filas con varios
          inputs cada una). Las columnas se declaran en field.columns.
          El value es Array<{ [colKey]: string }>.
          -->
          @case ('contacts') {
            <div class="services-wrapper full-width">
              <label class="services-label">{{ field.label }}</label>

              @for (row of getRepeaterRows(field); track $index; let rowIndex = $index) {
                <div class="contacts-row">
                  @for (col of field.columns; track col.key) {
                    @if (col.type === 'date') {
                      <!-- Columna de fecha: datepicker con calendario. -->
                      <mat-form-field appearance="outline" class="contacts-cell">
                        <mat-label>{{ col.label }}</mat-label>
                        <input
                          matInput
                          [matDatepicker]="repeaterPicker"
                          [value]="toDate(row[col.key])"
                          (dateChange)="updateRepeaterCell(field, rowIndex, col.key, $event.value)" />
                        <mat-datepicker-toggle matSuffix [for]="repeaterPicker"></mat-datepicker-toggle>
                        <mat-datepicker #repeaterPicker></mat-datepicker>
                      </mat-form-field>
                    } @else {
                      <mat-form-field appearance="outline" class="contacts-cell">
                        <mat-label>{{ col.label }}</mat-label>
                        <input
                          matInput
                          [type]="repeaterInputType(col)"
                          [attr.min]="repeaterInputType(col) === 'number' ? 0 : null"
                          [value]="row[col.key] ?? ''"
                          (input)="updateRepeaterCell(field, rowIndex, col.key, $any($event.target).value)" />
                      </mat-form-field>
                    }
                  }
                  <button
                    mat-icon-button
                    color="warn"
                    type="button"
                    matTooltip="Quitar"
                    (click)="removeRepeaterRow(field, rowIndex)">
                    <mat-icon>delete</mat-icon>
                  </button>
                </div>
              }

              <button
                mat-stroked-button
                color="primary"
                type="button"
                class="services-add-btn"
                (click)="addRepeaterRow(field)">
                <mat-icon>add</mat-icon>
                {{ field.addLabel ?? 'Agregar' }}
              </button>
            </div>
          }

          <!--
          Carga de archivo (file). Sube vía UploadDownloadService y guarda
          en el form el path en S3. El botón muestra "Subir" o "Reemplazar"
          según si ya hay archivo, y un check verde indica "PDF cargado".
          -->
          @case ('file') {
            <div class="file-upload-wrapper full-width">
              <label class="file-upload-label">{{ field.label }}</label>

              <div class="file-upload-row">
                <button
                  mat-stroked-button
                  color="primary"
                  type="button"
                  (click)="openFileManager(field)">
                  <mat-icon>{{ hasFile(field) ? 'description' : 'upload_file' }}</mat-icon>
                  {{ hasFile(field) ? 'Ver / reemplazar documento' : 'Cargar documento' }}
                </button>

                @if (hasFile(field)) {
                  <span class="file-upload-status ok">
                    <mat-icon>check_circle</mat-icon>
                    {{ getFileName(field) || 'Documento cargado' }}
                  </span>
                  <button
                    mat-icon-button
                    color="warn"
                    type="button"
                    matTooltip="Quitar documento"
                    (click)="clearFile(field)">
                    <mat-icon>delete</mat-icon>
                  </button>
                } @else {
                  <span class="file-upload-status muted">
                    <mat-icon>cancel</mat-icon> Sin documento
                  </span>
                }
              </div>
            </div>
          }

          <!--
          Captura de fotos desde la cámara del dispositivo (camera).
          Abre la cámara con getUserMedia, permite capturar varias fotos y/o
          seleccionarlas, y las sube a S3. El valor es un arreglo de keys S3.
          -->
          @case ('camera') {
            <div class="file-upload-wrapper full-width">
              <label class="file-upload-label">{{ field.label }}</label>

              <!-- Móvil: usa la cámara nativa (capture). Escritorio: input sin capture. -->
              <input
                #photoInput
                type="file"
                hidden
                accept="image/*"
                [attr.capture]="isMobileDevice ? 'environment' : null"
                multiple
                (change)="onPhotosSelected(field, $event)" />

              <div class="file-upload-row">
                @if (isMobileDevice) {
                  <!-- En móvil, "Tomar foto" abre directamente la cámara del dispositivo. -->
                  <button
                    mat-stroked-button
                    color="primary"
                    type="button"
                    (click)="triggerFileInput(photoInput)">
                    <mat-icon>photo_camera</mat-icon> Tomar foto
                  </button>
                } @else {
                  <!-- En escritorio, vista previa en vivo con la webcam + selección de archivos. -->
                  <button
                    mat-stroked-button
                    color="primary"
                    type="button"
                    [disabled]="isCameraActive(field)"
                    (click)="startCamera(field)">
                    <mat-icon>photo_camera</mat-icon> Abrir cámara
                  </button>
                  <button
                    mat-stroked-button
                    color="primary"
                    type="button"
                    (click)="triggerFileInput(photoInput)">
                    <mat-icon>image</mat-icon> Subir fotos
                  </button>
                }
                @if (isUploadingFile(field)) {
                  <span class="file-upload-status">
                    <mat-icon class="spin">progress_activity</mat-icon> Subiendo...
                  </span>
                }
              </div>

              @if (!isMobileDevice && isCameraActive(field)) {
                <div class="camera-preview">
                  <video
                    #cameraVideo
                    autoplay
                    playsinline
                    muted
                    class="camera-video"
                  [srcObject]="cameraStream(field)"></video>
                  <div class="camera-actions">
                    <button mat-flat-button color="primary" type="button"
                      (click)="capturePhoto(field, cameraVideo)">
                      <mat-icon>camera</mat-icon> Capturar
                    </button>
                    @if (hasMultipleCameras()) {
                      <button mat-stroked-button color="primary" type="button"
                        [matTooltip]="activeCameraLabel(field)"
                        (click)="switchCamera(field)">
                        <mat-icon>cameraswitch</mat-icon> Cambiar cámara
                      </button>
                    }
                    <button mat-stroked-button color="warn" type="button"
                      (click)="stopCamera(field)">
                      <mat-icon>close</mat-icon> Cerrar cámara
                    </button>
                  </div>
                  @if (hasMultipleCameras()) {
                    <span class="camera-label">{{ activeCameraLabel(field) }}</span>
                  }
                </div>
              }

              @if (getPhotos(field).length > 0) {
                <div class="photo-thumbs">
                  @for (key of getPhotos(field); track $index; let i = $index) {
                    <div class="photo-thumb">
                      <mat-icon>image</mat-icon>
                      <span class="photo-name">{{ photoLabel(key) }}</span>
                      <button mat-icon-button color="primary" type="button"
                        matTooltip="Ver / descargar"
                        (click)="viewPhoto(field, i)">
                        <mat-icon>visibility</mat-icon>
                      </button>
                      <button mat-icon-button color="warn" type="button"
                        matTooltip="Quitar foto"
                        (click)="removePhoto(field, i)">
                        <mat-icon>delete</mat-icon>
                      </button>
                    </div>
                  }
                </div>
              }
            </div>
          }

          <!--
          Lista de items con varios dropdowns + una cantidad por fila
          (itemsList). Usado para los proveedores del contrato:
          proveedor + equipo + cantidad. La suma de cantidades se valida
          contra field.maxFromField.
          -->
          @case ('itemsList') {
            <div class="services-wrapper full-width">
              <label class="services-label">{{ field.label }}</label>

              @for (row of getItemsListRows(field); track $index; let rowIndex = $index) {
                <div class="contacts-row">
                  @for (col of field.selectColumns; track col.key) {
                    <mat-form-field appearance="outline" class="contacts-cell">
                      <mat-label>{{ col.label }}</mat-label>
                      <input
                        matInput
                        type="text"
                        placeholder="Buscar..."
                        [matAutocomplete]="itemAuto"
                        [value]="itemCellDisplay(field, col, rowIndex, row)"
                        (input)="onItemCellInput(field, rowIndex, col.key, $any($event.target).value)"
                        (blur)="onItemCellBlur(field, rowIndex, col.key)" />
                      <mat-icon matSuffix>arrow_drop_down</mat-icon>
                      <mat-autocomplete
                        #itemAuto="matAutocomplete"
                        (optionSelected)="onItemCellSelected(field, rowIndex, col.key, $event.option.value)">
                        @for (opt of itemCellFilteredOptions(field, col, rowIndex); track opt.value) {
                          <mat-option [value]="opt.value">{{ opt.label }}</mat-option>
                        }
                      </mat-autocomplete>
                    </mat-form-field>
                  }

                  <mat-form-field appearance="outline" class="contacts-cell">
                    <mat-label>{{ itemsListQtyLabel(field) }}</mat-label>
                    <input
                      matInput
                      type="number"
                      min="0"
                      [value]="row[itemsListQtyKey(field)] ?? ''"
                      (input)="updateItemsListCell(field, rowIndex, itemsListQtyKey(field), $any($event.target).value)" />
                  </mat-form-field>

                  <button
                    mat-icon-button
                    color="warn"
                    type="button"
                    matTooltip="Quitar"
                    (click)="removeItemsListRow(field, rowIndex)">
                    <mat-icon>delete</mat-icon>
                  </button>
                </div>
              }

              <button
                mat-stroked-button
                color="primary"
                type="button"
                class="services-add-btn"
                (click)="addItemsListRow(field)">
                <mat-icon>add</mat-icon>
                {{ field.addLabel ?? 'Agregar' }}
              </button>

              @if (itemsListMax(field) !== null) {
                <div class="items-list-summary" [class.exceeded]="itemsListExceeded(field)">
                  <mat-icon>{{ itemsListExceeded(field) ? 'error' : 'functions' }}</mat-icon>
                  <span>
                    Total: {{ itemsListSum(field) }} / {{ itemsListMax(field) }}
                  </span>
                </div>
                @if (itemsListExceeded(field)) {<mat-error class="items-list-error">
                    La suma de cantidades no puede ser mayor al número de equipos del contrato.
                  </mat-error>}
              }
            </div>
          }

          <!-- Tabla con información -->
          @case ('table') {
            <div class="table-container">
              <h3>{{ field.label }}</h3>
              <table mat-table [dataSource]="field.tableConfig?.data || []" class="mat-elevation-z2">
                @for (col of field.tableConfig?.columns; track col) {
                  <ng-container [matColumnDef]="col">
                    <th mat-header-cell *matHeaderCellDef>{{ col | titlecase }}</th>
                    <td mat-cell *matCellDef="let element">{{ element[col] }}</td>
                  </ng-container>
                }
                <tr mat-header-row *matHeaderRowDef="field.tableConfig?.columns"></tr>
                <tr mat-row *matRowDef="let row; columns: field.tableConfig?.columns;"></tr>
              </table>
            </div>
          }

        }
      }
    }

    <!-- Template reutilizable para inputs de texto, número, email y rfid -->
    <ng-template #inputTemplate let-field="field" let-type="type">
      <mat-form-field
        appearance="outline"
        class="full-width"
        [class.uppercase-input]="field.uppercase && (field.type === 'text' || field.type === 'rfid')">
        <mat-label>{{ fieldLabel(field) }}</mat-label>
        <input
          matInput
          [type]="type"
          [formControlName]="field.key"
          [attr.min]="type === 'number' ? 0 : null"
          [attr.maxlength]="field.type === 'rfid' ? 24 : null"
          [attr.placeholder]="field.type === 'rfid' ? '24 caracteres' : null"
          />
        @if (field.type === 'rfid') {
          <mat-hint align="end">{{ (form.get(field.key)?.value || '').length }}/24</mat-hint>
          @if (form.get(field.key)?.hasError('minlength') || form.get(field.key)?.hasError('maxlength')) {<mat-error>La etiqueta debe tener 24 caracteres</mat-error>}
        }
        @if (form.get(field.key)?.hasError('required')) {<mat-error>Este campo es requerido</mat-error>}
      </mat-form-field>
    </ng-template>

  </form>
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button mat-button (click)="onCancel()">Cancelar</button>
  <button mat-flat-button color="primary" (click)="onSave()" [disabled]="saveDisabled">
    Aceptar
  </button>
</mat-dialog-actions>